import {
  adminChangePasswordInput,
  adminResetPinInput,
  api,
  queryKeys,
  type AdminChangePasswordInput,
  type AdminResetPinInput,
  type User,
  type UserStatus,
} from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { KeyRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { formatDate, statusTone } from '../../lib/format';
import { useTableQuery } from '../../hooks/useTableQuery';
import { Alert, Button, Chip, FormGroup, Input, Modal } from '../../ui';

const FILTERS = ['all', 'active', 'inactive', 'blocked'] as const;
type Filter = (typeof FILTERS)[number];

const TABS = FILTERS.map((f) => ({
  label: f[0].toUpperCase() + f.slice(1),
  to: `/members/${f}`,
}));

const col = createColumnHelper<User>();

/**
 * Quick credential reset, launched straight from the members list — no
 * navigation to the member's detail page required. Admin resets never ask for
 * the old password/PIN (they're one-way hashed; there is no "old value" to
 * verify), unlike the member's own self-service change.
 */
function CredentialsModal({ member, onClose }: { member: User; onClose: () => void }) {
  const [tab, setTab] = useState<'password' | 'pin'>('password');

  const passwordForm = useForm<AdminChangePasswordInput>({
    resolver: zodResolver(adminChangePasswordInput),
    defaultValues: { password: '', password_confirmation: '' },
  });
  const pinForm = useForm<AdminResetPinInput>({
    resolver: zodResolver(adminResetPinInput),
    defaultValues: { transaction_pin: '', transaction_pin_confirmation: '' },
  });

  const changePassword = useMutation({
    mutationFn: (values: AdminChangePasswordInput) => api.admin.changeMemberPassword(member.id, values),
    onSuccess: () => passwordForm.reset(),
  });
  const resetPin = useMutation({
    mutationFn: (values: AdminResetPinInput) => api.admin.resetMemberPin(member.id, values),
    onSuccess: () => pinForm.reset(),
  });

  return (
    <Modal open onClose={onClose} title={`Reset credentials — ${member.user_code}`} size="sm">
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button
          type="button"
          size="sm"
          variant={tab === 'password' ? 'gold' : 'outline-gold'}
          onClick={() => setTab('password')}
        >
          Login Password
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tab === 'pin' ? 'gold' : 'outline-gold'}
          onClick={() => setTab('pin')}
        >
          Transaction PIN
        </Button>
      </div>

      {tab === 'password' ? (
        <form
          onSubmit={passwordForm.handleSubmit((values) => changePassword.mutate(values))}
          noValidate
        >
          {changePassword.isSuccess ? <Alert tone="success">Password updated.</Alert> : null}
          {changePassword.error ? (
            <Alert tone="danger">{(changePassword.error as Error).message}</Alert>
          ) : null}
          <FormGroup label="New Password" error={passwordForm.formState.errors.password?.message}>
            <Input type="password" autoComplete="new-password" {...passwordForm.register('password')} />
          </FormGroup>
          <FormGroup
            label="Confirm New Password"
            error={passwordForm.formState.errors.password_confirmation?.message}
          >
            <Input
              type="password"
              autoComplete="new-password"
              {...passwordForm.register('password_confirmation')}
            />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={changePassword.isPending}>
            {changePassword.isPending ? 'Saving…' : 'Set New Password'}
          </Button>
        </form>
      ) : (
        <form onSubmit={pinForm.handleSubmit((values) => resetPin.mutate(values))} noValidate>
          {resetPin.isSuccess ? <Alert tone="success">Transaction PIN updated.</Alert> : null}
          {resetPin.error ? <Alert tone="danger">{(resetPin.error as Error).message}</Alert> : null}
          <FormGroup label="New PIN" error={pinForm.formState.errors.transaction_pin?.message}>
            <Input type="password" {...pinForm.register('transaction_pin')} />
          </FormGroup>
          <FormGroup
            label="Confirm New PIN"
            error={pinForm.formState.errors.transaction_pin_confirmation?.message}
          >
            <Input type="password" {...pinForm.register('transaction_pin_confirmation')} />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={resetPin.isPending}>
            {resetPin.isPending ? 'Saving…' : 'Set New PIN'}
          </Button>
        </form>
      )}
    </Modal>
  );
}

export function MemberList() {
  const { filter = 'all' } = useParams<{ filter: Filter }>();
  const status = (FILTERS as readonly string[]).includes(filter) ? filter : 'all';
  const { query, setPage } = useTableQuery({ defaultSort: 'created_at' });
  const [credentialsFor, setCredentialsFor] = useState<User | null>(null);

  const listQuery = useMemo(
    () => ({ ...query, status: status as UserStatus | 'all' }),
    [query, status],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.members(listQuery),
    queryFn: () => api.users.list(listQuery),
  });

  const columns = useMemo(
    () => [
      col.accessor('user_code', {
        header: 'User ID',
        // Opens the member's Achievement Reward tracker (client requirement:
        // clicking a member ID anywhere in the admin panel should open their
        // tracker) — the profile view is still one click away via the
        // "View" action below, so nothing is lost.
        cell: (info) => <Link to={`/income/achievement-reward?member=${info.getValue()}`}>{info.getValue()}</Link>,
      }),
      col.accessor('name', { header: 'Name' }),
      col.accessor('email', { header: 'Email' }),
      col.accessor('sponsor_code', { header: 'Sponsor', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('rank_code', { header: 'Rank', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('status', {
        header: 'Account',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('is_activated', {
        header: 'Package Activated',
        cell: (i) => (i.getValue() ? <Chip tone="success">Yes</Chip> : <Chip tone="danger">No</Chip>),
      }),
      col.accessor('created_at', { header: 'Joined', cell: (i) => formatDate(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <div style={{ display: 'flex', gap: 6 }}>
            <Chip as="a" tone="gold" href={`/members/view/${info.row.original.id}`}>
              View
            </Chip>
            <Chip
              as="button"
              type="button"
              tone="gold"
              onClick={() => setCredentialsFor(info.row.original)}
              title="Reset password / transaction PIN"
            >
              <KeyRound size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              Credentials
            </Chip>
          </div>
        ),
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Members" tabs={TABS} />
      {credentialsFor ? (
        <CredentialsModal member={credentialsFor} onClose={() => setCredentialsFor(null)} />
      ) : null}
      <DataTable<User>
        title={`${status[0].toUpperCase()}${status.slice(1)} Members`}
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
